import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import loreCornerDetail from '../../assets/lore/lore_details.png';
import loreButton from '../../assets/lore/lore_button.png';
import { pickStableLoreIllustration } from '../../utils/loreAssets';

function CornerDetail({ className = '', rotationClass = '' }) {
  return (
    <img
      src={loreCornerDetail}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`pointer-events-none absolute h-[52px] w-[52px] select-none opacity-80 ${rotationClass} ${className}`}
    />
  );
}

export function LoreModal({ open, onClose, text, showIllustration = true }) {
  const [selectedIllustration, setSelectedIllustration] = React.useState(null);
  const [shouldScroll, setShouldScroll] = React.useState(false);
  const [maxTextHeight, setMaxTextHeight] = React.useState(0);
  const textRef = React.useRef(null);

  React.useEffect(() => {
    if (!open || !showIllustration) return;
    setSelectedIllustration(pickStableLoreIllustration(text));
  }, [open, text, showIllustration]);

  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  React.useEffect(() => {
    if (!open) {
      setShouldScroll(false);
      setMaxTextHeight(0);
      return;
    }

    const measureScroll = () => {
      const element = textRef.current;
      if (!element) return;

      const safeModalHeight = Math.max(320, Math.floor(window.innerHeight * 0.78));
      const nextMaxTextHeight = Math.max(140, safeModalHeight - 230);
      setMaxTextHeight(nextMaxTextHeight);
      setShouldScroll(element.scrollHeight > nextMaxTextHeight + 1);
    };

    const frameId = window.requestAnimationFrame(measureScroll);
    window.addEventListener('resize', measureScroll);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', measureScroll);
    };
  }, [open, text, showIllustration]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.14, ease: 'easeOut' }}
            onMouseDown={onClose}
          />

          {showIllustration && !!selectedIllustration && (
            <div className="pointer-events-none absolute inset-0 z-[71] flex items-center justify-center px-6 py-6">
              <motion.img
                src={selectedIllustration}
                alt=""
                aria-hidden="true"
                draggable={false}
                className="block max-h-[56vh] w-auto max-w-[72vw] select-none object-contain object-center opacity-95 drop-shadow-[0_12px_24px_rgba(0,0,0,0.75)] md:max-w-[520px]"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              />
            </div>
          )}

          <motion.div
            className="relative z-[72] w-auto max-w-[92vw] border border-white/0 bg-black/80 px-16 py-10 text-white shadow-[0_20px_60px_rgba(0,0,0,0.7)]"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <CornerDetail className="left-4 top-4" rotationClass="rotate-180" />
            <CornerDetail className="right-4 top-4" rotationClass="-rotate-90" />
            <CornerDetail className="left-4 bottom-4" rotationClass="rotate-90" />
            <CornerDetail className="right-4 bottom-4" />

            <div
              className="relative z-[73] mx-auto flex min-h-0 w-auto max-w-full flex-col items-center"
              style={{
                width: 'fit-content',
                minWidth: 'min(320px, calc(92vw - 10rem))',
                maxWidth: 'min(440px, calc(92vw - 10rem))',
              }}
            >
              <div
                ref={textRef}
                className="w-full text-center text-[18px] leading-[1.35] whitespace-pre-wrap text-white/90"
                style={{
                  fontFamily: 'Karma, serif',
                  maxWidth: 'min(440px, calc(92vw - 10rem))',
                  maxHeight: shouldScroll ? `${maxTextHeight}px` : 'none',
                  overflowY: shouldScroll ? 'auto' : 'visible',
                  paddingRight: shouldScroll ? '0.5rem' : '0',
                }}
              >
                {text}
              </div>

              <button
                type="button"
                onClick={onClose}
                className="relative mt-6 h-[68px] w-[250px] appearance-none border-0 bg-transparent p-0 text-white shadow-none outline-none transition hover:opacity-90 active:scale-[0.99]"
                style={{ fontFamily: 'Karma, serif' }}
              >
                <img
                  src={loreButton}
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                  className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
                />
                <span className="relative z-[1] flex h-full w-full translate-y-[2px] items-center justify-center text-[18px]">
                  Continue
                </span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
